import { FRAME_SURFACE_ATTRIBUTE, FrameSurface } from './surfaces';

/**
 * A label is an authored, bounded token: lower-case letters, digits, `-` and
 * `_`, at most 48 characters. Anything else is not a label.
 */
const SAFE_LABEL = /^[a-z0-9][a-z0-9_-]{0,47}$/;

/** The label for anything that is not a marked element. */
export const FALLBACK_LABEL = 'other';

/**
 * Validates an authored marker value. A marker is a static string written in
 * JSX, so it either matches the safe token shape or it is a mistake; a
 * mismatch collapses to `other` rather than leaking whatever was there.
 *
 * The authored token IS the wire value (lower-cased, nothing else): a marker
 * written `session_list` reaches Datadog as `session_list`, equal to
 * `FrameSurface.SESSION_LIST` and to what a dashboard filter types. Product
 * markers are snake_case like the rest of the metadata keys.
 */
export function sanitizeFrameLabel(raw: string): string {
  const value = raw.trim().toLowerCase();
  return SAFE_LABEL.test(value) ? value : FALLBACK_LABEL;
}

/**
 * The surface label for an event target.
 *
 * Only two sources are trusted: a `data-frame-surface` marker on the element
 * or its nearest marked ancestor, and otherwise the element's tag name.
 * `document` / `window` (a page scroll) is `document`.
 *
 * Test ids, `data-region` and element ids are deliberately NOT used. They are
 * authored strings in the common case, but several are built from runtime
 * data (a patient row's test id carries the patient id; an uploaded XML
 * form's toggle carries the field name), and no stripping rule can tell a
 * name from an identifier reliably. A label that reaches Datadog must be
 * bounded and free of anything a user typed, so the choice is: marked
 * surfaces get their marker, everything else gets a tag.
 */
export function describeFrameTarget(target: EventTarget | null): FrameSurface {
  if (!target || !(target instanceof Element)) return FrameSurface.DOCUMENT;

  const marked = target.closest(`[${FRAME_SURFACE_ATTRIBUTE}]`);
  const explicit = marked?.getAttribute(FRAME_SURFACE_ATTRIBUTE);
  if (explicit) return sanitizeFrameLabel(explicit);

  return sanitizeFrameLabel(target.tagName);
}
